import { type ReactNode, useCallback, useEffect, useState } from 'react';
import {
    FiActivity,
    FiBarChart2,
    FiBell,
    FiBook,
    FiChevronsLeft,
    FiChevronsRight,
    FiClipboard,
    FiCreditCard,
    FiDollarSign,
    FiFileText,
    FiGrid,
    FiHelpCircle,
    FiList,
    FiLogOut,
    FiMenu,
    FiMinusCircle,
    FiPlusCircle,
    FiSettings,
    FiShield,
    FiTrendingUp,
    FiUsers,
    FiX
} from 'react-icons/fi';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts';
import { usePreferences } from '../../../contexts/PreferencesContext';
import logoClaro from '../../../assets/Logo_claro.png';
import logoEscuro from '../../../assets/Logo_escuro.png';
import styles from './Sidebar.module.css';

interface NavItem {
    path: string;
    labelKey: string;
    icon: ReactNode;
    separator?: boolean;
}

const NAV_ITEMS: NavItem[] = [
    { path: '/home', labelKey: 'nav.menu', icon: <FiGrid size={18} /> },
    { path: '/lancar-receita', labelKey: 'nav.lancarReceitas', icon: <FiPlusCircle size={18} /> },
    { path: '/lancar-despesa', labelKey: 'nav.lancarDespesas', icon: <FiMinusCircle size={18} /> },
    { path: '/lancamentos', labelKey: 'nav.historico', icon: <FiList size={18} /> },
    { path: '/cashflow', labelKey: 'nav.cashflow', icon: <FiTrendingUp size={18} /> },
    { path: '/graficos', labelKey: 'nav.graficos', icon: <FiBarChart2 size={18} /> },
    { path: '/reports', labelKey: 'nav.relatorios', icon: <FiFileText size={18} /> },
    { path: '/plano-contas', labelKey: 'nav.planoContas', icon: <FiBook size={18} /> },
    { path: '/settings', labelKey: 'nav.configuracoes', icon: <FiSettings size={18} />, separator: true },
];

export function Sidebar() {
    const { user, logout, isAdmin, isGerente, isDemo, isDemoRoute, canAccess } = useAuth();
    const { t, prefs } = usePreferences();
    const darkMode = prefs.darkMode;
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem('sidebar-collapsed') === 'true'; }
        catch { return false; }
    });

    const toggleCollapsed = useCallback(() => {
        setCollapsed(prev => {
            const next = !prev;
            try { localStorage.setItem('sidebar-collapsed', String(next)); } catch { }
            document.documentElement.setAttribute('data-sidebar-collapsed', String(next));
            return next;
        });
    }, []);

    // Sync CSS attribute on mount
    useEffect(() => {
        document.documentElement.setAttribute('data-sidebar-collapsed', String(collapsed));
    }, [collapsed]);

    // Close sidebar on route change (mobile)
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    // Close sidebar on Escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMobileOpen(false);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    // Lock body scroll when mobile sidebar open
    useEffect(() => {
        if (mobileOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileOpen]);

    const toggleMobile = useCallback(() => setMobileOpen(prev => !prev), []);

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const filteredNavItems = isAdmin
        ? []
        : isDemo
            ? NAV_ITEMS.filter((item) => isDemoRoute(item.path))
            : NAV_ITEMS.filter((item) => canAccess(item.path));

    return (
        <>
            <button
                className={styles.hamburger}
                onClick={toggleMobile}
                aria-label={t('nav.abrirMenu')}
            >
                <FiMenu size={22} />
            </button>

            {mobileOpen && (
                <div
                    className={styles.overlay}
                    onClick={() => setMobileOpen(false)}
                />
            )}

            <aside className={`${styles.sidebar} ${mobileOpen ? styles.sidebarOpen : ''} ${collapsed ? styles.collapsed : ''}`}>
                <div className={styles.sidebarTopRow}>
                    <div className={styles.logo}>
                        <img
                            src={darkMode ? logoClaro : logoEscuro}
                            alt="Financeiro Diário"
                            className={styles.logoImg}
                        />
                    </div>
                    <button
                        className={styles.collapseBtn}
                        onClick={toggleCollapsed}
                        aria-label={collapsed ? t('nav.expandirMenu') : t('nav.recolherMenu')}
                        title={collapsed ? t('nav.expandirMenu') : t('nav.recolherMenu')}
                    >
                        {collapsed ? <FiChevronsRight size={18} /> : <FiChevronsLeft size={18} />}
                    </button>
                    <button
                        className={styles.closeBtn}
                        onClick={() => setMobileOpen(false)}
                        aria-label={t('nav.fecharMenu')}
                    >
                        <FiX size={20} />
                    </button>
                </div>

                {/* <div className={styles.trialBadge}>
                <div className={styles.trialText}>Teste Gratuito</div>
                <div className={styles.trialProgress}>
                    <div className={styles.trialProgressBar} style={{ width: '50%' }} />
                </div>
                <div className={styles.trialDays}>15 dias</div>
            </div> */}

                <nav className={styles.nav}>
                    {filteredNavItems.map((item) => (
                        <div key={item.path}>
                            {item.separator && <div className={styles.navSeparator} />}
                            <NavLink
                                to={item.path}
                                className={({ isActive }) =>
                                    `${styles.navItem} ${isActive ? styles.active : ''}`
                                }
                                title={t(item.labelKey)}
                            >
                                <span className={styles.navIcon}>{item.icon}</span>
                                <span className={styles.navLabel}>{t(item.labelKey)}</span>
                            </NavLink>
                        </div>
                    ))}

                    {isAdmin && (
                        <>
                            <NavLink
                                to="/admin"
                                end
                                className={({ isActive }) =>
                                    `${styles.navItem} ${styles.adminItem} ${isActive ? styles.active : ''}`
                                }
                                title="Admin"
                            >
                                <span className={styles.navIcon}><FiShield size={18} /></span>
                                <span className={styles.navLabel}>Admin</span>
                            </NavLink>
                            <NavLink
                                to="/admin/clientes"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${styles.adminItem} ${isActive ? styles.active : ''}`
                                }
                                title="Clientes"
                            >
                                <span className={styles.navIcon}><FiUsers size={18} /></span>
                                <span className={styles.navLabel}>Clientes</span>
                            </NavLink>
                            <NavLink
                                to="/admin/assinaturas"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${styles.adminItem} ${isActive ? styles.active : ''}`
                                }
                                title="Assinaturas"
                            >
                                <span className={styles.navIcon}><FiCreditCard size={18} /></span>
                                <span className={styles.navLabel}>Assinaturas</span>
                            </NavLink>
                            <NavLink
                                to="/admin/planos"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${styles.adminItem} ${isActive ? styles.active : ''}`
                                }
                                title="Planos"
                            >
                                <span className={styles.navIcon}><FiDollarSign size={18} /></span>
                                <span className={styles.navLabel}>Planos</span>
                            </NavLink>
                            <NavLink
                                to="/admin/notificacoes"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${styles.adminItem} ${isActive ? styles.active : ''}`
                                }
                                title="Notificações"
                            >
                                <span className={styles.navIcon}><FiBell size={18} /></span>
                                <span className={styles.navLabel}>Notificações</span>
                            </NavLink>
                            <NavLink
                                to="/admin/tracking"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${styles.adminItem} ${isActive ? styles.active : ''}`
                                }
                                title="Tracking"
                            >
                                <span className={styles.navIcon}><FiActivity size={18} /></span>
                                <span className={styles.navLabel}>Tracking</span>
                            </NavLink>
                            <NavLink
                                to="/admin/pesquisa-onboarding"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${styles.adminItem} ${isActive ? styles.active : ''}`
                                }
                                title="Pesquisa de Onboarding"
                            >
                                <span className={styles.navIcon}><FiClipboard size={18} /></span>
                                <span className={styles.navLabel}>Pesquisa de Onboarding</span>
                            </NavLink>
                            <NavLink
                                to="/admin/configuracoes"
                                className={({ isActive }) =>
                                    `${styles.navItem} ${styles.adminItem} ${isActive ? styles.active : ''}`
                                }
                                title="Configurações Admin"
                            >
                                <span className={styles.navIcon}><FiSettings size={18} /></span>
                                <span className={styles.navLabel}>Configurações Admin</span>
                            </NavLink>
                        </>
                    )}

                    {/* TODO: Gestão de Usuários para GERENTE — em desenvolvimento
                    {isGerente && !isAdmin && (
                        <NavLink
                            to="/gestao-usuarios"
                            className={({ isActive }) =>
                                `${styles.navItem} ${styles.adminItem} ${isActive ? styles.active : ''}`
                            }
                            title={t('nav.gestaoUsuarios')}
                        >
                            <span className={styles.navIcon}><FiUsers size={18} /></span>
                            <span className={styles.navLabel}>{t('nav.gestaoUsuarios')}</span>
                        </NavLink>
                    )}
                    */}
                </nav>

                <div className={styles.footer}>
                    <div
                        className={styles.footerLink}
                        onClick={() => navigate('/help')}
                        style={{ cursor: 'pointer' }}
                    >
                        <FiHelpCircle size={16} />
                        <span>{t('nav.ajuda')}</span>
                    </div>
                </div>
                <div className={styles.userSection}>
                    <div className={styles.avatar}>
                        {user ? getInitials(user.name) : 'US'}
                    </div>
                    <div className={styles.userInfo}>
                        <div className={styles.userName}>{user?.name || 'Usuário'}</div>
                        <div className={styles.userEmail}>{user?.email || 'email@email.com'}</div>
                    </div>
                    <button className={styles.userMenu} onClick={logout} title="Sair">
                        <FiLogOut size={16} />
                    </button>
                </div>
            </aside>
        </>
    );
}